import { v4 as uuidv4 } from 'uuid'
import { USER } from '@/modules/account/store/types'
import * as types from './types'
import {
  CHAT_STATUS_IDLE,
  CHAT_STATUS_LOADING,
  CHAT_STATUS_READY,
  CHAT_STATUS_UNAVAILABLE,
  CHAT_STATUS_FORBIDDEN,
  CHAT_STATUS_ERROR,
} from './state'
import * as chatApi from '../services/chatHttp'
import * as coreDirectory from '../services/coreDirectory'
import {
  getStaffToken,
  clearStaffToken,
  CHAT_ERROR_UNAVAILABLE,
  CHAT_ERROR_FORBIDDEN,
  CHAT_ERROR_SESSION_CLOSED,
} from '../services/staffToken'
import ChatSocket from '../services/chatSocket'
import {
  LOCAL_STATUS_SENDING,
  LOCAL_STATUS_ERROR,
  buildOptimisticMessage,
  isOwnMessage,
  maxSeq,
  minSeq,
} from '../utils/messages'
import { INBOX_SCOPES, applyIncomingMessage } from '../utils/inbox'
import { applyParticipantFrame, invitableSearchParams } from '../utils/participants'

const INBOX_PAGE_SIZE = 20
const MESSAGES_PAGE_SIZE = 50
const CATCH_UP_PAGE_SIZE = 100
const CATCH_UP_MAX_PAGES = 10
const TYPING_TTL_MS = 5000
const INBOX_REFRESH_DEBOUNCE_MS = 1500

// Recursos não serializáveis ficam fora do state do Vuex.
let socket = null
// Incrementado no RESET: um INIT em andamento percebe o logout e não sobe o WS.
let sessionId = 0
let inboxRefreshTimer = null
const typingTimers = {}
const readInflight = new Set()

const isPageVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'

export const statusFromError = error => {
  const code = error && error.code
  const status = error && error.status
  if (code === CHAT_ERROR_UNAVAILABLE || status === 503) return CHAT_STATUS_UNAVAILABLE
  if (code === CHAT_ERROR_FORBIDDEN || status === 403) return CHAT_STATUS_FORBIDDEN
  if (code === CHAT_ERROR_SESSION_CLOSED) return CHAT_STATUS_IDLE
  return CHAT_STATUS_ERROR
}

const sendErrorMessage = error => {
  if (error && error.status === 403) return 'Você não pode enviar mensagens nesta conversa.'
  if (error && (error.status === 422 || error.status === 400)) return 'Mensagem inválida.'
  if (error && error.code === CHAT_ERROR_UNAVAILABLE) return 'Chat indisponível no momento.'
  return 'Não foi possível enviar.'
}

const clearAllTimers = () => {
  if (inboxRefreshTimer) clearTimeout(inboxRefreshTimer)
  inboxRefreshTimer = null
  Object.keys(typingTimers).forEach(id => {
    clearTimeout(typingTimers[id])
    delete typingTimers[id]
  })
  readInflight.clear()
}

const scheduleInboxRefresh = dispatch => {
  if (inboxRefreshTimer) clearTimeout(inboxRefreshTimer)
  inboxRefreshTimer = setTimeout(() => {
    inboxRefreshTimer = null
    dispatch(types.REFRESH_INBOXES).catch(() => {})
  }, INBOX_REFRESH_DEBOUNCE_MS)
}

const isInAnyInbox = (state, conversationId) => Object.keys(state.inbox)
  .some(scope => state.inbox[scope].ids.includes(conversationId))

const loadLatestMessages = async ({ commit }, conversationId) => {
  commit(types.MUTATE_MESSAGES_STATE, { conversationId, loading: true, error: false })
  try {
    const page = await chatApi.fetchMessages(conversationId, { limit: MESSAGES_PAGE_SIZE })
    commit(types.MUTATE_MERGE_MESSAGES, { conversationId, messages: page.messages || [] })
    commit(types.MUTATE_MESSAGES_STATE, {
      conversationId, loading: false, loaded: true, hasMoreOlder: !!page.hasMore,
    })
  } catch (error) {
    commit(types.MUTATE_MESSAGES_STATE, { conversationId, loading: false, error: true })
    throw error
  }
}

// Busca o que chegou depois do maior seq conhecido (reconexão / reabertura).
const catchUpConversation = async (context, conversationId) => {
  const { state, commit } = context
  const entry = state.messages[conversationId]
  if (!entry || !entry.loaded) {
    await loadLatestMessages(context, conversationId)
    return
  }
  let afterSeq = maxSeq(entry.items)
  for (let i = 0; i < CATCH_UP_MAX_PAGES; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const page = await chatApi.fetchMessages(conversationId, { afterSeq, limit: CATCH_UP_PAGE_SIZE })
    const messages = page.messages || []
    commit(types.MUTATE_MERGE_MESSAGES, { conversationId, messages })
    if (!page.hasMore || !messages.length) break
    afterSeq = maxSeq(messages)
  }
}

const applyMessageToInbox = ({ state, commit, getters }, message, { forceOwn = false } = {}) => {
  const { conversationId } = message
  const conversation = state.conversations[conversationId]
  if (!conversation) return false
  const isOpenAndVisible = state.openConversationId === conversationId && state.viewVisible && isPageVisible()
  const isOwn = forceOwn || isOwnMessage(message, getters[types.MY_PRINCIPAL])
  commit(types.MUTATE_UPSERT_CONVERSATIONS, [applyIncomingMessage(conversation, message, { isOpenAndVisible, isOwn })])
  commit(types.MUTATE_MOVE_CONVERSATION_TO_TOP, conversationId)
  return isInAnyInbox(state, conversationId)
}

const deliverMessage = async (context, conversationId, clientMessageId, body) => {
  const { commit, dispatch } = context
  try {
    const response = await chatApi.sendMessage(conversationId, { clientMessageId, body })
    const message = response && response.message
    if (message) {
      commit(types.MUTATE_MERGE_MESSAGES, { conversationId, messages: [message] })
      applyMessageToInbox(context, message, { forceOwn: true })
      dispatch(types.MARK_OPEN_AS_READ).catch(() => {})
    }
    return true
  } catch (error) {
    commit(types.MUTATE_MESSAGE_LOCAL_STATUS, {
      conversationId, clientMessageId, localStatus: LOCAL_STATUS_ERROR, errorMessage: sendErrorMessage(error),
    })
    return false
  }
}

const startSocket = ({ commit, dispatch }) => {
  if (socket) return
  socket = new ChatSocket({
    getTicket: () => chatApi.requestWsTicket(),
    getConnectionInfo: () => getStaffToken(),
    onFrame: frame => dispatch(types.HANDLE_FRAME, frame),
    onStatus: status => commit(types.MUTATE_CONNECTION_STATUS, status),
    onReconnected: () => dispatch(types.CATCH_UP).catch(() => {}),
    onFatal: error => commit(types.MUTATE_STATUS, statusFromError(error)),
  })
  socket.start()
}

export default {
  // Idempotente: chamado pelo plugin (login/perfil) e pela tela do chat.
  [types.INIT]: async ({ state, commit, dispatch }) => {
    if (state.status === CHAT_STATUS_LOADING || state.status === CHAT_STATUS_READY) return
    const mySession = sessionId
    commit(types.MUTATE_STATUS, CHAT_STATUS_LOADING)
    try {
      await getStaffToken()
      await dispatch(types.REFRESH_INBOXES)
      if (mySession !== sessionId) return
      commit(types.MUTATE_STATUS, CHAT_STATUS_READY)
      startSocket({ commit, dispatch })
    } catch (error) {
      if (mySession !== sessionId) return
      commit(types.MUTATE_STATUS, statusFromError(error))
    }
  },
  // Logout / perda da permissão: derruba WS, esquece o token e limpa o estado.
  [types.RESET]: ({ commit }) => {
    sessionId += 1
    if (socket) socket.stop()
    socket = null
    clearAllTimers()
    clearStaffToken()
    commit(types.MUTATE_RESET)
  },
  [types.SET_ACTIVE_SCOPE]: ({ commit }, scope) => {
    commit(types.MUTATE_ACTIVE_SCOPE, scope)
  },
  [types.FETCH_INBOX]: async ({ commit }, { scope, page = 0 }) => {
    const mySession = sessionId
    commit(types.MUTATE_INBOX_LOADING, { scope, loading: true })
    try {
      const response = await chatApi.fetchInbox({ scope, page, size: INBOX_PAGE_SIZE })
      // Resposta chegou depois do logout: descarta (não repovoa o state limpo).
      if (mySession !== sessionId) return []
      const items = (response && response.items) || []
      commit(types.MUTATE_UPSERT_CONVERSATIONS, items)
      commit(types.MUTATE_INBOX_PAGE, {
        scope,
        ids: items.map(item => item.conversationId),
        page,
        hasMore: items.length >= INBOX_PAGE_SIZE,
        append: page > 0,
      })
      return items
    } catch (error) {
      commit(types.MUTATE_INBOX_LOADING, { scope, loading: false, error: true })
      throw error
    }
  },
  [types.REFRESH_INBOXES]: ({ dispatch }) => Promise.all(
    INBOX_SCOPES.map(scope => dispatch(types.FETCH_INBOX, { scope, page: 0 })),
  ),
  [types.OPEN_CONVERSATION_BY_ID]: async (context, conversationId) => {
    const { commit, dispatch } = context
    commit(types.MUTATE_OPEN_CONVERSATION_ID, conversationId)
    const conversation = await chatApi.fetchConversation(conversationId)
    commit(types.MUTATE_UPSERT_CONVERSATIONS, [conversation])
    await catchUpConversation(context, conversationId)
    dispatch(types.MARK_OPEN_AS_READ).catch(() => {})
    return conversation
  },
  [types.CLOSE_CONVERSATION]: ({ commit }) => {
    commit(types.MUTATE_OPEN_CONVERSATION_ID, null)
  },
  // A partir da ficha do cliente. Erros (404 sem app / 403 não participa) sobem para a tela.
  [types.OPEN_CONVERSATION_BY_CUSTOMER]: async ({ commit }, coreCustomerId) => {
    const conversation = await chatApi.fetchConversationByCustomer(coreCustomerId)
    commit(types.MUTATE_UPSERT_CONVERSATIONS, [conversation])
    return conversation
  },
  [types.LOAD_OLDER_MESSAGES]: async ({ state, commit }) => {
    const conversationId = state.openConversationId
    const entry = conversationId ? state.messages[conversationId] : null
    if (!entry || entry.loadingOlder || !entry.hasMoreOlder) return 0
    const beforeSeq = minSeq(entry.items)
    if (beforeSeq === null) return 0
    const conversation = state.conversations[conversationId] || {}
    const readFromSeq = Number(conversation.readFromSeq) || 0
    if (beforeSeq <= readFromSeq + 1) {
      commit(types.MUTATE_MESSAGES_STATE, { conversationId, hasMoreOlder: false })
      return 0
    }
    commit(types.MUTATE_MESSAGES_STATE, { conversationId, loadingOlder: true })
    try {
      const page = await chatApi.fetchMessages(conversationId, { beforeSeq, limit: MESSAGES_PAGE_SIZE })
      const messages = page.messages || []
      commit(types.MUTATE_MERGE_MESSAGES, { conversationId, messages })
      const oldest = minSeq(messages)
      commit(types.MUTATE_MESSAGES_STATE, {
        conversationId,
        loadingOlder: false,
        hasMoreOlder: !!page.hasMore && messages.length > 0 && (oldest === null || oldest > readFromSeq + 1),
      })
      return messages.length
    } catch (error) {
      commit(types.MUTATE_MESSAGES_STATE, { conversationId, loadingOlder: false })
      throw error
    }
  },
  // Após reconectar o WS: catch-up da conversa aberta + inbox atualizado.
  [types.CATCH_UP]: async context => {
    const { state, commit, dispatch } = context
    const conversationId = state.openConversationId
    const tasks = [dispatch(types.REFRESH_INBOXES).catch(() => {})]
    if (conversationId) {
      tasks.push(
        chatApi.fetchConversation(conversationId)
          .then(conversation => commit(types.MUTATE_UPSERT_CONVERSATIONS, [conversation]))
          .then(() => catchUpConversation(context, conversationId))
          .then(() => dispatch(types.MARK_OPEN_AS_READ))
          .catch(() => {}),
      )
    }
    await Promise.all(tasks)
  },
  [types.SEND_MESSAGE]: async (context, { conversationId, body }) => {
    const { commit, getters, rootGetters } = context
    const text = (body || '').trim()
    if (!conversationId || !text) return false
    const user = rootGetters[USER] || {}
    const clientMessageId = uuidv4()
    commit(types.MUTATE_MERGE_MESSAGES, {
      conversationId,
      messages: [buildOptimisticMessage({
        conversationId,
        clientMessageId,
        body: text,
        principal: getters[types.MY_PRINCIPAL],
        senderName: user.name,
      })],
    })
    return deliverMessage(context, conversationId, clientMessageId, text)
  },
  // Reenvio idempotente: mesmo clientMessageId (servidor devolve 200 se já tinha gravado).
  [types.RETRY_MESSAGE]: (context, { conversationId, clientMessageId }) => {
    const { state, commit } = context
    const entry = state.messages[conversationId]
    const pending = entry && entry.items.find(message => !message.id && message.clientMessageId === clientMessageId)
    if (!pending) return Promise.resolve(false)
    commit(types.MUTATE_MESSAGE_LOCAL_STATUS, {
      conversationId, clientMessageId, localStatus: LOCAL_STATUS_SENDING, errorMessage: undefined,
    })
    return deliverMessage(context, conversationId, clientMessageId, pending.body)
  },
  [types.DISCARD_MESSAGE]: ({ commit }, { conversationId, clientMessageId }) => {
    commit(types.MUTATE_REMOVE_PENDING_MESSAGE, { conversationId, clientMessageId })
  },
  // Marca como lida a conversa aberta, só se a tela estiver visível.
  [types.MARK_OPEN_AS_READ]: async ({ state, commit }) => {
    const conversationId = state.openConversationId
    if (!conversationId || !state.viewVisible || !isPageVisible()) return
    const entry = state.messages[conversationId]
    const seq = entry ? maxSeq(entry.items) : 0
    if (!seq) return
    const conversation = state.conversations[conversationId] || {}
    const alreadySent = state.myReadSeq[conversationId] || 0
    if (seq <= alreadySent && !(Number(conversation.unreadCount) > 0)) return
    if (readInflight.has(conversationId)) return
    readInflight.add(conversationId)
    try {
      const response = await chatApi.markRead(conversationId, seq)
      commit(types.MUTATE_MY_READ, {
        conversationId,
        lastReadSeq: (response && response.lastReadSeq) || seq,
      })
    } finally {
      readInflight.delete(conversationId)
    }
  },
  [types.SET_VIEW_VISIBLE]: ({ commit, dispatch }, visible) => {
    commit(types.MUTATE_VIEW_VISIBLE, visible)
    if (visible) dispatch(types.MARK_OPEN_AS_READ).catch(() => {})
  },
  [types.SEND_TYPING]: (_, conversationId) => {
    if (socket && conversationId) socket.sendTyping(conversationId)
  },
  [types.HANDLE_FRAME]: (context, frame) => {
    const { state, commit, dispatch } = context
    const data = (frame && frame.data) || {}

    if (frame.type === 'message.created') {
      const { conversationId } = data
      if (!conversationId) return
      // Só mantém timeline de conversas já abertas; as demais carregam ao abrir.
      if (state.messages[conversationId]) {
        commit(types.MUTATE_MERGE_MESSAGES, { conversationId, messages: [data] })
      }
      if (state.typing[conversationId]) commit(types.MUTATE_TYPING, { conversationId })
      const listed = applyMessageToInbox(context, data)
      // Conversa nova ou fora das páginas carregadas: recarrega o inbox (com debounce).
      if (!listed) scheduleInboxRefresh(dispatch)
      if (state.openConversationId === conversationId) dispatch(types.MARK_OPEN_AS_READ).catch(() => {})
      return
    }

    if (frame.type === 'read.updated') {
      // STAFF pode ser outro participante; só a leitura do cliente vira indicador.
      if (data.readerType === 'CUSTOMER' && data.conversationId) {
        commit(types.MUTATE_CUSTOMER_READ, { conversationId: data.conversationId, lastReadSeq: data.lastReadSeq })
      }
      return
    }

    if (frame.type === 'typing') {
      const { conversationId, displayName } = data
      if (!conversationId || !displayName) return
      commit(types.MUTATE_TYPING, { conversationId, displayName, until: Date.now() + TYPING_TTL_MS })
      if (typingTimers[conversationId]) clearTimeout(typingTimers[conversationId])
      typingTimers[conversationId] = setTimeout(() => {
        delete typingTimers[conversationId]
        commit(types.MUTATE_TYPING, { conversationId })
      }, TYPING_TTL_MS)
    }

    if (frame.type === 'participant.added' || frame.type === 'participant.removed') {
      const { conversationId, participant } = data
      if (!conversationId || !participant) return
      const me = context.getters[types.MY_PRINCIPAL]
      const isMe = !!me && participant.principal === me
      if (frame.type === 'participant.removed' && isMe) {
        // Saí, fui removido, o convite venceu ou fui desligado: a conversa some daqui.
        if (state.openConversationId === conversationId) commit(types.MUTATE_OPEN_CONVERSATION_ID, null)
        commit(types.MUTATE_REMOVE_FROM_INBOXES, conversationId)
        return
      }
      const conversation = state.conversations[conversationId]
      if (conversation) {
        commit(types.MUTATE_UPSERT_CONVERSATIONS, [applyParticipantFrame(conversation, frame.type, participant)])
      }
      // Convidado agora: entra em "Direcionadas a mim".
      if (frame.type === 'participant.added' && isMe) scheduleInboxRefresh(dispatch)
    }
    // 'error' e tipos desconhecidos: ignorados (o servidor fecha o socket se for grave).
  },

  // ── "Direcionar" ─────────────────────────────────────────────────────────────
  [types.FETCH_INVITABLE_USERS]: (_, filters) => coreDirectory.fetchInvitableUsers(invitableSearchParams(filters || {})),
  [types.FETCH_DEPARTMENTS]: () => coreDirectory.fetchDepartments(),
  // O frame participant.added também chega, mas aplicar já evita esperar o WS.
  [types.INVITE_PARTICIPANT]: async ({ state, commit }, { conversationId, payload }) => {
    const participant = await chatApi.inviteParticipant(conversationId, payload)
    const conversation = state.conversations[conversationId]
    if (conversation) {
      commit(types.MUTATE_UPSERT_CONVERSATIONS, [applyParticipantFrame(conversation, 'participant.added', participant)])
    }
    return participant
  },
  [types.REMOVE_PARTICIPANT]: async ({ state, commit, getters }, { conversationId, participant }) => {
    await chatApi.removeParticipant(conversationId, participant.participantId)
    const me = getters[types.MY_PRINCIPAL]
    if (me && participant.principal === me) {
      if (state.openConversationId === conversationId) commit(types.MUTATE_OPEN_CONVERSATION_ID, null)
      commit(types.MUTATE_REMOVE_FROM_INBOXES, conversationId)
      return
    }
    const conversation = state.conversations[conversationId]
    if (conversation) {
      commit(types.MUTATE_UPSERT_CONVERSATIONS, [applyParticipantFrame(conversation, 'participant.removed', participant)])
    }
  },
}
